<template>
  <div>
    <TransitionRoot as="template" :show="open">
      <Dialog as="div" class="relative z-50" @close="closeSlideOver">
        <div v-if="!fadeBackground" class="fixed inset-0" />
        <TransitionChild
          v-else
          as="template"
          enter="ease-in-out duration-500"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in-out duration-500"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500/50 transition-opacity" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-hidden">
          <div class="absolute inset-0 overflow-hidden">
            <div
              class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full"
              :class="{ 'pl-10 sm:pl-16': paddingLeft }"
            >
              <TransitionChild
                as="template"
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enter-from="translate-x-full"
                enter-to="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leave-from="translate-x-0"
                leave-to="translate-x-full"
              >
                <DialogPanel
                  class="pointer-events-auto w-screen"
                  :class="large ? 'max-w-xl' : 'max-w-md'"
                >
                  <div
                    class="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
                  >
                    <div
                      :id="id + '-scroll-container'"
                      class="h-0 flex-1 overflow-y-auto"
                    >
                      <div
                        class="px-4 py-6 sm:px-6"
                        :class="
                          headerBackground === 'indigo'
                            ? 'bg-indigo-700'
                            : 'bg-gray-50 border-b border-b-gray-200'
                        "
                      >
                        <div class="flex items-center justify-between">
                          <DialogTitle
                            class="truncate text-base font-semibold leading-6"
                            :class="
                              headerBackground === 'indigo'
                                ? 'text-white'
                                : 'text-gray-900'
                            "
                          >
                            {{ title }}
                          </DialogTitle>
                          <div class="ml-3 flex h-7 items-center">
                            <button
                              type="button"
                              class="-mr-2 inline-flex items-center justify-center rounded-md p-2 focus:outline-none"
                              :class="
                                headerBackground === 'indigo'
                                  ? 'bg-indigo-700 text-indigo-200 hover:bg-indigo-500 hover:text-white'
                                  : 'text-gray-400 hover:bg-gray-200 hover:text-gray-500'
                              "
                              @click="closeSlideOver"
                            >
                              <span class="sr-only">Close panel</span>
                              <XMarkIcon
                                class="block size-6"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                        <div v-if="subtitle" class="mt-1">
                          <p
                            class="text-sm"
                            :class="
                              headerBackground === 'indigo'
                                ? 'text-indigo-300'
                                : 'text-gray-500'
                            "
                          >
                            {{ subtitle }}
                          </p>
                        </div>
                      </div>
                      <div
                        :class="{ 'relative animate-pulse': loadingOverlay }"
                      >
                        <LoadingOverlay v-if="loadingOverlay" />
                        <div
                          :class="{
                            'px-4 py-6 sm:px-6': !customBodyPadding,
                            'space-y-6': !customBodyContentSpacing,
                            'text-[0]': removeBodyWhitespace,
                          }"
                        >
                          <slot />
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="$slots.footer"
                      class="shrink-0"
                      :class="{
                        'flex justify-end space-x-2 px-4 py-6 sm:px-6':
                          !customFooterClasses,
                      }"
                    >
                      <slot name="footer" />
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script lang="ts" setup>
// DOCS: https://rollouter.atlassian.net/wiki/spaces/RCP/pages/7864438/SlideOver

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

type HeaderBackground = 'indigo' | 'gray';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  fadeBackground: {
    type: Boolean,
    default: true,
  },
  paddingLeft: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    default: null,
  },
  headerBackground: {
    type: String as PropType<HeaderBackground>,
    default: 'gray' as HeaderBackground,
  },
  customBodyPadding: {
    type: Boolean,
    default: false,
  },
  customBodyContentSpacing: {
    type: Boolean,
    default: false,
  },
  removeBodyWhitespace: {
    type: Boolean,
    default: false,
  },
  customFooterClasses: {
    type: Boolean,
    default: false,
  },
  closeable: {
    type: Boolean,
    default: true,
  },
  loadingOverlay: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
    required: true,
  },
  large: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const open = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const closeSlideOver = () => {
  if (props.closeable) {
    open.value = false;
  }
};
</script>
